/**
 * MapView2D - 2D map using MapLibre GL JS
 * Renders order polygon with OSM/Satellite basemap
 */

import { useRef, useEffect, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import { useAtlasStore } from '@/stores/atlasStore';
import { OSM_PMTILES_URL } from '@/services/api';
import { selectBasemapProvider, type BasemapStyle } from '@/services/basemaps';

// Active basemap provider chosen at build time via VITE_BASEMAP_PROVIDER
// (default 'auto': MapTiler if VITE_MAPTILER_KEY is set, public otherwise).
// All three styles within a provider share the same tileSize, so switching
// between mapa/satelite/terreno at runtime is a single setTiles() call.
const BASEMAPS = selectBasemapProvider();

// The atlasStore uses 'osm'/'satellite'/'terrain' as legacy keys; map them
// to the registry's BasemapStyle keys ('mapa'/'satelite'/'terreno').
const STYLE_ALIAS: Record<string, BasemapStyle> = {
  osm: 'mapa',
  mapa: 'mapa',
  satellite: 'satelite',
  satelite: 'satelite',
  terrain: 'terreno',
  terreno: 'terreno',
};

function basemapFor(name: string) {
  return BASEMAPS[STYLE_ALIAS[name] || 'mapa'];
}

export function MapView2D() {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const polygonAddedRef = useRef(false);

  const orderPolygon = useAtlasStore((s) => s.orderPolygon);
  const orderBounds = useAtlasStore((s) => s.orderBounds);
  const showPolygon = useAtlasStore((s) => s.showPolygon);
  const baseLayer = useAtlasStore((s) => s.baseLayer);
  const viewMode = useAtlasStore((s) => s.viewMode);
  const setMapCenter = useAtlasStore((s) => s.setMapCenter);
  const setMapZoom = useAtlasStore((s) => s.setMapZoom);
  const mapCenter = useAtlasStore((s) => s.mapCenter);
  const mapZoom = useAtlasStore((s) => s.mapZoom);
  // Create-order draw flow
  const drawing = useAtlasStore((s) => s.drawing);
  const draftPoints = useAtlasStore((s) => s.draftPoints);
  const draftGeometry = useAtlasStore((s) => s.draftGeometry);
  const addDraftPoint = useAtlasStore((s) => s.addDraftPoint);
  // Terreno Base raster layers (standard products, LOLA/DGT provider)
  const mdtVisible = useAtlasStore((s) => s.mdtVisible);
  const mdtOpacity = useAtlasStore((s) => s.mdtOpacity);
  const hillshadeVisible = useAtlasStore((s) => s.hillshadeVisible);
  const hillshadeOpacity = useAtlasStore((s) => s.hillshadeOpacity);
  const hillshadeHypsoVisible = useAtlasStore((s) => s.hillshadeHypsoVisible);
  const hillshadeHypsoOpacity = useAtlasStore((s) => s.hillshadeHypsoOpacity);
  // Morfologia
  const slopeVisible = useAtlasStore((s) => s.slopeVisible);
  const slopeOpacity = useAtlasStore((s) => s.slopeOpacity);
  const aspectVisible = useAtlasStore((s) => s.aspectVisible);
  const aspectOpacity = useAtlasStore((s) => s.aspectOpacity);
  const geomorphonsVisible = useAtlasStore((s) => s.geomorphonsVisible);
  const geomorphonsOpacity = useAtlasStore((s) => s.geomorphonsOpacity);
  const windExpositionVisible = useAtlasStore((s) => s.windExpositionVisible);
  const windExpositionOpacity = useAtlasStore((s) => s.windExpositionOpacity);
  // Contour layer (vector)
  const contoursVisible = useAtlasStore((s) => s.contoursVisible);
  const contoursOpacity = useAtlasStore((s) => s.contoursOpacity);
  // Flow Accumulation (raster)
  const flowAccVisible = useAtlasStore((s) => s.flowAccVisible);
  const flowAccOpacity = useAtlasStore((s) => s.flowAccOpacity);
  // Streams layer (vector)
  const streamsVisible = useAtlasStore((s) => s.streamsVisible);
  const streamsOpacity = useAtlasStore((s) => s.streamsOpacity);
  // Ridges layer (vector)
  const ridgesVisible = useAtlasStore((s) => s.ridgesVisible);
  const ridgesOpacity = useAtlasStore((s) => s.ridgesOpacity);
  // Basins layer (vector polygons)
  const basinsVisible = useAtlasStore((s) => s.basinsVisible);
  const basinsOpacity = useAtlasStore((s) => s.basinsOpacity);
  // Primary valleys (vector polygons)
  const primaryValleysVisible = useAtlasStore((s) => s.primaryValleysVisible);
  const primaryValleysOpacity = useAtlasStore((s) => s.primaryValleysOpacity);
  // Primary ridges (vector polygons)
  const primaryRidgesVisible = useAtlasStore((s) => s.primaryRidgesVisible);
  const primaryRidgesOpacity = useAtlasStore((s) => s.primaryRidgesOpacity);
  // Erosion risk (raster)
  const erosionRiskVisible = useAtlasStore((s) => s.erosionRiskVisible);
  const erosionRiskOpacity = useAtlasStore((s) => s.erosionRiskOpacity);
  // Keypoints (vector points)
  const keypointsVisible = useAtlasStore((s) => s.keypointsVisible);
  const keypointsOpacity = useAtlasStore((s) => s.keypointsOpacity);
  // Flood zones layer (vector polygons)
  const floodZonesVisible = useAtlasStore((s) => s.floodZonesVisible);
  const floodZonesOpacity = useAtlasStore((s) => s.floodZonesOpacity);
  // Institutional layers (PR5b)
  const cadastroDgtVisible = useAtlasStore((s) => s.cadastroDgtVisible);
  const cadastroDgtOpacity = useAtlasStore((s) => s.cadastroDgtOpacity);
  const bupiRggVisible = useAtlasStore((s) => s.bupiRggVisible);
  const bupiRggOpacity = useAtlasStore((s) => s.bupiRggOpacity);
  const freguesiasVisible = useAtlasStore((s) => s.freguesiasVisible);
  const freguesiasOpacity = useAtlasStore((s) => s.freguesiasOpacity);
  const concelhosVisible = useAtlasStore((s) => s.concelhosVisible);
  const concelhosOpacity = useAtlasStore((s) => s.concelhosOpacity);
  const renVisible = useAtlasStore((s) => s.renVisible);
  const renOpacity = useAtlasStore((s) => s.renOpacity);
  const renExclusoesVisible = useAtlasStore((s) => s.renExclusoesVisible);
  const renExclusoesOpacity = useAtlasStore((s) => s.renExclusoesOpacity);
  const ranVisible = useAtlasStore((s) => s.ranVisible);
  const ranOpacity = useAtlasStore((s) => s.ranOpacity);
  // OSM Context — Roads & Buildings
  const roadsVisible = useAtlasStore((s) => s.roadsVisible);
  const roadsOpacity = useAtlasStore((s) => s.roadsOpacity);
  const buildingsVisible = useAtlasStore((s) => s.buildingsVisible);
  const buildingsOpacity = useAtlasStore((s) => s.buildingsOpacity);
  const landuseVisible = useAtlasStore((s) => s.landuseVisible);
  const landuseOpacity = useAtlasStore((s) => s.landuseOpacity);
  const waterwaysVisible = useAtlasStore((s) => s.waterwaysVisible);
  const waterwaysOpacity = useAtlasStore((s) => s.waterwaysOpacity);
  const adminBoundsVisible = useAtlasStore((s) => s.adminBoundsVisible);
  const adminBoundsOpacity = useAtlasStore((s) => s.adminBoundsOpacity);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;

    const basemap = basemapFor(baseLayer);

    // Register PMTiles protocol for OSM context layers
    const pmtilesProtocol = new Protocol();
    maplibregl.addProtocol('pmtiles', pmtilesProtocol.tile);

    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
        sources: {
          basemap: {
            type: 'raster',
            tiles: [basemap.url],
            tileSize: basemap.tileSize,
            attribution: basemap.attribution,
          },
        },
        layers: [
          {
            id: 'basemap-layer',
            type: 'raster',
            source: 'basemap',
          },
        ],
      },
      center: mapCenter,
      zoom: mapZoom,
    });

    map.addControl(new maplibregl.NavigationControl(), 'bottom-right');
    map.addControl(new maplibregl.ScaleControl(), 'bottom-right');

    // Sync camera to store on move
    map.on('moveend', () => {
      const center = map.getCenter();
      setMapCenter([center.lng, center.lat]);
      setMapZoom(map.getZoom());
    });

    mapRef.current = map;
    (window as any).__maplibreMap = map;

    // Cadastro parcel popup: when the cadastro layer is on, clicking a parcel
    // shows NIC + area + location names. Reads MVT tile properties; the
    // freguesia/concelho/distrito names appear once migration 032 + a Martin
    // restart land (until then it shows NIC + area only).
    const cadastroPopup = new maplibregl.Popup({ closeButton: true, closeOnClick: false, maxWidth: '260px' });
    const escHtml = (s: string) =>
      s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
    // Artigo matricial display is feature-flagged (legal-gated, EB-26). The DGT
    // cadastre tiles carry only the NIC, so the artigo is fetched per click from
    // the analyzer (point lookup on geo.cadastro_artigo). BUPi carries its
    // numeromatriz in the tile directly (open data), so no fetch needed there.
    const ARTIGO_DGT_ENABLED = import.meta.env.VITE_ARTIGO_DGT_ENABLED === 'true';
    const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';
    let clickSeq = 0;
    map.on('click', (e) => {
      const st = useAtlasStore.getState();
      if (st.drawing) return;
      const myseq = ++clickSeq;

      // Cadastro DGT parcel: NIC + area + location names (+ async artigo).
      let cadVals: { nic: string; nicRaw: string | null; areaHa: string | null; loc: string } | null = null;
      if (st.cadastroDgtVisible && map.getLayer('cadastro-dgt-fill')) {
        const fs = map.queryRenderedFeatures(e.point, { layers: ['cadastro-dgt-fill'] });
        if (fs.length) {
          const p = (fs[0].properties || {}) as Record<string, unknown>;
          const nicRaw = (p.national_cadastral_reference as string) || null;
          cadVals = {
            nic: nicRaw || (p.label as string) || '—',
            nicRaw,
            areaHa: p.area_value != null ? `${(Number(p.area_value) / 10000).toFixed(2)} ha` : null,
            loc: [p.freguesia, p.concelho, p.distrito].filter(Boolean).join(' · '),
          };
        }
      }
      const buildCad = (sec: string | null, art: string | null) =>
        cadVals
          ? '<div style="font-weight:600">Cadastro DGT</div>' +
            `<div>NIC ${escHtml(cadVals.nic)}</div>` +
            (sec ? `<div>Secção ${escHtml(sec)}</div>` : '') +
            (art ? `<div>Artigo matricial ${escHtml(art)}</div>` : '') +
            (cadVals.areaHa ? `<div style="color:#555">${cadVals.areaHa}</div>` : '') +
            (cadVals.loc ? `<div style="color:#555">${escHtml(cadVals.loc)}</div>` : '')
          : '';

      // BUPi RGG parcel: artigo matricial (numeromatriz, open data) + dicofre.
      let bupiBlock = '';
      if (st.bupiRggVisible && map.getLayer('bupi-rgg-fill')) {
        const fs = map.queryRenderedFeatures(e.point, { layers: ['bupi-rgg-fill'] });
        if (fs.length) {
          const p = (fs[0].properties || {}) as Record<string, unknown>;
          const artigo = (p.numeromatriz as string) || '—';
          const dicofre = (p.dicofre as string) || null;
          bupiBlock =
            '<div style="font-weight:600">BUPi (RGG)</div>' +
            `<div>Artigo matricial ${escHtml(artigo)}</div>` +
            (dicofre ? `<div style="color:#555">Freguesia (DICOFRE) ${escHtml(dicofre)}</div>` : '');
        }
      }

      const render = (sec: string | null, art: string | null) => {
        if (myseq !== clickSeq) return; // a newer click happened; ignore stale fetch
        const parts = [buildCad(sec, art), bupiBlock].filter(Boolean);
        if (!parts.length) { cadastroPopup.remove(); return; }
        cadastroPopup
          .setLngLat(e.lngLat)
          .setHTML(
            '<div style="font:13px Inter,system-ui,sans-serif;color:#1C1B19;line-height:1.4">' +
            parts.join('<hr style="border:none;border-top:1px solid #eee;margin:6px 0"/>') +
            '</div>',
          )
          .addTo(map);
      };

      if (!cadVals && !bupiBlock) { cadastroPopup.remove(); return; }
      render(null, null); // show immediately; secção/artigo fill in when the lookup returns

      if (ARTIGO_DGT_ENABLED && cadVals?.nicRaw) {
        fetch(`${API_BASE}/cadastro/artigo?nic=${encodeURIComponent(cadVals.nicRaw)}`)
          .then((r) => (r.ok ? r.json() : null))
          .then((d) => {
            if (d && d.artigo) render(d.seccao || null, d.artigo);
          })
          .catch(() => {});
      }
    });

    // Subscribe to Zustand store to add raster layers as tile URLs arrive
    // Standard products (LOLA/DGT provider produces high-res output)
    const RASTER_LAYERS = [
      { id: 'mdt-layer', urlKey: 'mdtTileUrl' as const, visKey: 'mdtVisible' as const, opKey: 'mdtOpacity' as const, zoomKey: 'mdtZoomRange' as const },
      { id: 'hillshade-layer', urlKey: 'hillshadeTileUrl' as const, visKey: 'hillshadeVisible' as const, opKey: 'hillshadeOpacity' as const, zoomKey: 'hillshadeZoomRange' as const },
      { id: 'hillshade-hypso-layer', urlKey: 'hillshadeHypsoTileUrl' as const, visKey: 'hillshadeHypsoVisible' as const, opKey: 'hillshadeHypsoOpacity' as const, zoomKey: 'hillshadeHypsoZoomRange' as const },
      { id: 'slope-layer', urlKey: 'slopeTileUrl' as const, visKey: 'slopeVisible' as const, opKey: 'slopeOpacity' as const, zoomKey: 'slopeZoomRange' as const },
      { id: 'aspect-layer', urlKey: 'aspectTileUrl' as const, visKey: 'aspectVisible' as const, opKey: 'aspectOpacity' as const, zoomKey: 'aspectZoomRange' as const },
      { id: 'geomorphons-layer', urlKey: 'geomorphonsTileUrl' as const, visKey: 'geomorphonsVisible' as const, opKey: 'geomorphonsOpacity' as const, zoomKey: 'geomorphonsZoomRange' as const },
      { id: 'wind-relief-layer', urlKey: 'windReliefTileUrl' as const, visKey: 'windExpositionVisible' as const, opKey: 'windExpositionOpacity' as const, zoomKey: 'windReliefZoomRange' as const },
      { id: 'wind-hillshade-layer', urlKey: 'windHillshadeTileUrl' as const, visKey: 'windExpositionVisible' as const, opKey: 'windExpositionOpacity' as const, zoomKey: 'windHillshadeZoomRange' as const },
      { id: 'wind-exposition-layer', urlKey: 'windExpositionTileUrl' as const, visKey: 'windExpositionVisible' as const, opKey: 'windExpositionOpacity' as const, zoomKey: 'windExpositionZoomRange' as const },
      { id: 'erosion-risk-layer', urlKey: 'erosionRiskTileUrl' as const, visKey: 'erosionRiskVisible' as const, opKey: 'erosionRiskOpacity' as const, zoomKey: 'erosionRiskZoomRange' as const },
      { id: 'flow-acc-layer', urlKey: 'flowAccTileUrl' as const, visKey: 'flowAccVisible' as const, opKey: 'flowAccOpacity' as const, zoomKey: 'flowAccZoomRange' as const },
    ];

    const addMissingLayers = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      for (const layer of RASTER_LAYERS) {
        const tileUrl = state[layer.urlKey];
        if (!tileUrl || map.getSource(layer.id)) continue;
        // Wait for the layer's zoomRange to load before creating the source.
        // MapLibre's RasterTileSource has no public API to update minzoom/
        // maxzoom after creation, so adding the source without a zoom range
        // makes MapLibre fan out tile requests at every zoom (including z=8
        // country-view). The COG only has overviews for z=14-16; z=8 forces
        // a full-raster downsample that takes 60s+ and times out at the
        // Cloudflare edge with no CORS headers. addMissingLayers re-runs on
        // every store update, so this just defers the add by ~50-200ms.
        const zoomRange = layer.zoomKey ? (state as any)[layer.zoomKey] : null;
        if (layer.zoomKey && !zoomRange) continue;
        const sourceOpts: any = { type: 'raster', tiles: [tileUrl], tileSize: 256 };
        if (zoomRange) {
          sourceOpts.minzoom = zoomRange.minzoom;
          sourceOpts.maxzoom = zoomRange.maxzoom;
        }
        if (state.orderBounds) {
          // Pad the polygon bbox by 25% of its larger dimension: raster
          // outputs are clipped to a buffer that FOLLOWS the polygon
          // (migration 038, ~10% of sqrt(area)), which can extend past the
          // raw bbox. Without the pad MapLibre would never request the edge
          // tiles and the buffered raster would appear cut off. A few extra
          // empty edge-tile requests are harmless (TiTiler masks them).
          const [w, s, e, n] = state.orderBounds;
          const pad = 0.25 * Math.max(e - w, n - s);
          sourceOpts.bounds = [w - pad, s - pad, e + pad, n + pad];
        }
        map.addSource(layer.id, sourceOpts);
        // Wind hillshade must be BELOW wind exposition — insert before it if it exists
        let beforeLayer: string | undefined;
        if (layer.id === 'wind-hillshade-layer' && map.getLayer('wind-exposition-layer')) {
          beforeLayer = 'wind-exposition-layer';
        } else {
          beforeLayer = map.getLayer('order-polygon-fill') ? 'order-polygon-fill' : undefined;
        }
        map.addLayer({
          id: layer.id, type: 'raster', source: layer.id,
          paint: { 'raster-opacity': state[layer.visKey] ? state[layer.opKey] : 0 },
        }, beforeLayer);
        console.log(`${layer.id} added to 2D map`);
      }
      // Ensure wind hillshade is always below wind exposition (may arrive in wrong order)
      if (map.getLayer('wind-hillshade-layer') && map.getLayer('wind-exposition-layer')) {
        const layers = map.getStyle().layers.map((l: any) => l.id);
        if (layers.indexOf('wind-hillshade-layer') > layers.indexOf('wind-exposition-layer')) {
          map.moveLayer('wind-hillshade-layer', 'wind-exposition-layer');
        }
      }

      // MDT relief overlay: a native MapLibre hillshade layer over the MDT
      // colour ramp, shaded from the SAME DTM COG re-encoded as Terrarium RGB
      // by TiTiler (raster-dem source). MapLibre has no multiply blend, so this
      // native hillshade is how we get shaded relief on the tint. It is added
      // AFTER mdt-layer and before order-polygon-fill, so it sits directly above
      // the colour and below the polygon outline. Same zoom range + padded
      // bounds guards as the raster layers (avoids z8 full-raster downsamples).
      if (state.mdtDemTileUrl && state.mdtZoomRange && !map.getSource('mdt-dem')) {
        const demSource: any = {
          type: 'raster-dem',
          tiles: [state.mdtDemTileUrl],
          tileSize: 256,
          encoding: 'terrarium',
          minzoom: state.mdtZoomRange.minzoom,
          maxzoom: state.mdtZoomRange.maxzoom,
        };
        if (state.orderBounds) {
          const [w, s, e, n] = state.orderBounds;
          const pad = 0.25 * Math.max(e - w, n - s);
          demSource.bounds = [w - pad, s - pad, e + pad, n + pad];
        }
        map.addSource('mdt-dem', demSource);
        const beforeMdt = map.getLayer('order-polygon-fill') ? 'order-polygon-fill' : undefined;
        map.addLayer({
          id: 'mdt-hillshade-layer',
          type: 'hillshade',
          source: 'mdt-dem',
          layout: { visibility: state.mdtVisible ? 'visible' : 'none' },
          paint: {
            'hillshade-exaggeration': 0.45,
            'hillshade-shadow-color': 'rgba(30, 20, 10, 0.55)',
            'hillshade-highlight-color': 'rgba(255, 255, 245, 0.35)',
            'hillshade-accent-color': 'rgba(0, 0, 0, 0)',
            'hillshade-illumination-direction': 315,
            'hillshade-illumination-anchor': 'map',
          },
        }, beforeMdt);
        // Keep it directly above the colour layer if both are present.
        if (map.getLayer('mdt-layer') && map.getLayer('mdt-hillshade-layer')) {
          map.moveLayer('mdt-hillshade-layer', beforeMdt);
        }
        console.log('mdt-hillshade-layer (relief) added to 2D map');
      }
    };

    // Add DGT contour vector layers when tile URL arrives
    const addContourDgtLayers = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      if (!state.contoursTileUrl || !state.contoursSourceLayer || !state.contoursItemId) return;
      if (map.getLayer('contours-dgt-5m-layer')) return;

      // Zone-aware opacity (migration 039): 'fringe' rows (ring between the
      // core clip buffer and 2x) render faint so lines fade out instead of
      // ending abruptly at the clip edge. Legacy rows have no zone => full.
      const zoneOpacity = (op: number) =>
        ['case', ['==', ['get', 'zone'], 'fringe'], op * 0.3, op] as any;

      if (!map.getSource('contours-dgt')) map.addSource('contours-dgt', {
        type: 'vector',
        tiles: [state.contoursTileUrl],
        // minzoom 10: below this a tile envelope spans ~40 km and would drag the
        // order's whole contour set through Martin for lines that are unreadable
        // anyway. z10 still fits a 10k ha order in one view.
        minzoom: 10,
        maxzoom: 16,  // Overzoom: reuse zoom-16 tiles at higher zooms to preserve 50cm detail
      });

      const beforeLayer = map.getLayer('order-polygon-fill') ? 'order-polygon-fill' : undefined;
      const itemId = state.contoursItemId;

      // 5m contour lines — black per Altimetria.qml spec
      map.addLayer({
        id: 'contours-dgt-5m-layer',
        type: 'line',
        source: 'contours-dgt',
        'source-layer': state.contoursSourceLayer,
        filter: ['all',
          ['==', ['to-string', ['get', 'order_item_id']], itemId],
          ['==', ['%', ['get', 'elevation'], 5], 0],
        ],
        paint: {
          'line-color': '#000000',
          'line-width': 1.2,
          'line-opacity': state.contoursVisible ? zoneOpacity(state.contoursOpacity) : 0,
        },
      }, beforeLayer);

      // 1m contour lines — visible at close zoom
      map.addLayer({
        id: 'contours-dgt-1m-layer',
        type: 'line',
        source: 'contours-dgt',
        'source-layer': state.contoursSourceLayer,
        filter: ['all',
          ['==', ['to-string', ['get', 'order_item_id']], itemId],
          ['==', ['%', ['get', 'elevation'], 1], 0],
          ['!=', ['%', ['get', 'elevation'], 5], 0],
        ],
        minzoom: 14,
        paint: {
          'line-color': '#000000',
          'line-width': 0.6,
          'line-opacity': state.contoursVisible ? zoneOpacity(state.contoursOpacity) : 0,
        },
      }, beforeLayer);

      // Elevation labels on 5m contours (per Altimetria.qml: black, 6pt Open Sans, mask 1.5mm)
      map.addLayer({
        id: 'contours-dgt-labels-layer',
        type: 'symbol',
        source: 'contours-dgt',
        'source-layer': state.contoursSourceLayer,
        filter: ['all',
          ['==', ['to-string', ['get', 'order_item_id']], itemId],
          ['==', ['%', ['get', 'elevation'], 10], 0],
          ['!=', ['get', 'zone'], 'fringe'],
        ],
        layout: {
          'symbol-placement': 'point',
          'text-field': ['to-string', ['get', 'elevation']],
          'text-font': ['Noto Sans Regular'],
          'text-size': 8,
          'text-allow-overlap': false,
          visibility: state.contoursVisible ? 'visible' : 'none',
        },
        paint: {
          'text-color': '#000000',
          'text-halo-color': '#fafafa',
          'text-halo-width': 1.5,
          'text-opacity': 1,
        },
      });

      console.log('Contour DGT vector layers added to 2D map');
    };

    // Add streams as a SINGLE continuous line layer (SAGA channel network).
    // EB-73 Phase 1: one layer with a smooth width taper + colour ramp driven by
    // stream_order, round caps/joins so confluences and tile-edge joins read as one
    // continuous drainage network (was 7 per-order layers => visible seams/gaps).
    const addStreamsLayers = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      if (!state.streamsTileUrl || !state.streamsSourceLayer || !state.streamsItemId) return;
      if (map.getLayer('streams-line')) return;

      if (!map.getSource('streams-src')) map.addSource('streams-src', {
        type: 'vector',
        tiles: [state.streamsTileUrl],
        minzoom: 0,
        maxzoom: 16,
      });

      // EB-73: draw ABOVE the parcel fill (no beforeId) so the blue 25% order
      // fill stops washing the lines out and the per-order colours read.
      const itemId = state.streamsItemId;
      const itemFilter = ['==', ['to-string', ['get', 'order_item_id']], itemId];

      // Per-order categorical colour (EB-73): each Strahler order a distinct cool
      // hue so orders 1-6 are tell-apart-able (width taper alone only separates
      // 1-2). Step expression on stream_order, '6+' catch-all = navy trunk. Stays
      // fully cool so it never reads as the warm festos layer.
      const colorByOrder = [
        'step', ['to-number', ['get', 'stream_order'], 1],
        '#a6cee3',        // order 1  light blue
        2, '#1f78b4',     // order 2  blue
        3, '#41b6c4',     // order 3  teal
        4, '#33a02c',     // order 4  green
        5, '#6a3d9a',     // order 5  purple
        6, '#000080',     // order 6+ navy
      ] as any;
      // Width tapers with order AND scales with zoom (hairline headwaters -> fat trunk).
      const widthByOrderZoom = [
        'interpolate', ['linear'], ['zoom'],
        11, ['interpolate', ['linear'], ['to-number', ['get', 'stream_order'], 1], 1, 0.6, 6, 2.6],
        16, ['interpolate', ['linear'], ['to-number', ['get', 'stream_order'], 1], 1, 1.3, 6, 6.0],
      ] as any;

      map.addLayer({
        id: 'streams-line',
        type: 'line',
        source: 'streams-src',
        'source-layer': state.streamsSourceLayer,
        filter: itemFilter as any,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': colorByOrder,
          'line-width': widthByOrderZoom,
          'line-opacity': state.streamsVisible ? state.streamsOpacity : 0,
        },
      });

      console.log('Streams line layer added to 2D map');
    };

    // Add ridges as a SINGLE continuous line layer (SAGA channel network on the
    // inverted DTM = watershed divides). EB-73: mirrors the streams Phase 1 render
    // (one layer, smooth width taper + colour ramp by order, round caps/joins) so
    // the festos read as one continuous network instead of 7 per-order seams.
    // Symmetric pair of streams: same width expression, red/brown ramp per QML.
    const addRidgesLayers = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      if (!state.ridgesTileUrl || !state.ridgesSourceLayer || !state.ridgesItemId) return;
      if (map.getLayer('ridges-line')) return;

      if (!map.getSource('ridges-src')) map.addSource('ridges-src', {
        type: 'vector',
        tiles: [state.ridgesTileUrl],
        minzoom: 0,
        maxzoom: 16,
      });

      // EB-73: draw ABOVE the parcel fill (no beforeId), same as streams.
      const itemId = state.ridgesItemId;
      const itemFilter = ['==', ['to-string', ['get', 'order_item_id']], itemId];

      // Per-order categorical colour (EB-73): warm YlOrRd ramp, each Strahler
      // order a distinct hue (orders 1-6 tell-apart-able). Step expression on
      // stream_order, '6+' catch-all = dark red trunk. Stays fully warm so it
      // never reads as the cool streams layer.
      const colorByOrder = [
        'step', ['to-number', ['get', 'stream_order'], 1],
        '#ffeda0',        // order 1  pale yellow
        2, '#fed976',     // order 2  yellow
        3, '#feb24c',     // order 3  amber
        4, '#fd8d3c',     // order 4  orange
        5, '#f03b20',     // order 5  red
        6, '#bd0026',     // order 6+ dark red
      ] as any;
      // Same width taper as streams: hairline headwaters -> fat trunk, scales with zoom.
      const widthByOrderZoom = [
        'interpolate', ['linear'], ['zoom'],
        11, ['interpolate', ['linear'], ['to-number', ['get', 'stream_order'], 1], 1, 0.6, 6, 2.6],
        16, ['interpolate', ['linear'], ['to-number', ['get', 'stream_order'], 1], 1, 1.3, 6, 6.0],
      ] as any;

      map.addLayer({
        id: 'ridges-line',
        type: 'line',
        source: 'ridges-src',
        'source-layer': state.ridgesSourceLayer,
        filter: itemFilter as any,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': colorByOrder,
          'line-width': widthByOrderZoom,
          'line-opacity': state.ridgesVisible ? state.ridgesOpacity : 0,
        },
      });

      console.log('Ridges line layer added to 2D map');
    };

    // Add basins fill + outline + label layers (polygon vector)
    const addBasinsLayers = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      if (!state.basinsTileUrl || !state.basinsSourceLayer || !state.basinsItemId) return;
      if (map.getLayer('basins-fill')) return;

      if (!map.getSource('basins-src')) map.addSource('basins-src', {
        type: 'vector',
        tiles: [state.basinsTileUrl],
        minzoom: 0,
        maxzoom: 16,
      });

      const itemId = state.basinsItemId;
      const itemFilter = ['==', ['to-string', ['get', 'order_item_id']], itemId];

      // Fill: Purples graduated by area (EB-80 v0.2, Miguel 05/04 + 06/02 decision).
      // Smaller basins lighter, larger darker. Distinct hue from the blue water
      // layers (streams, flow-accumulation) to avoid palette crowding. Breaks are
      // initial - tune on a real order. Drawn ABOVE the parcel fill (no beforeId).
      map.addLayer({
        id: 'basins-fill',
        type: 'fill',
        source: 'basins-src',
        'source-layer': state.basinsSourceLayer,
        filter: itemFilter,
        paint: {
          'fill-color': ['interpolate', ['linear'], ['to-number', ['get', 'area_ha'], 0],
            0.5, '#efedf5',
            20, '#9e9ac8',
            150, '#54278f',
          ],
          'fill-opacity': state.basinsVisible ? state.basinsOpacity : 0,
        },
      });

      // Outline: dark gray, thin (v0.2). Above the parcel fill.
      map.addLayer({
        id: 'basins-outline',
        type: 'line',
        source: 'basins-src',
        'source-layer': state.basinsSourceLayer,
        filter: itemFilter,
        paint: {
          'line-color': '#232323',
          'line-width': 1,
          'line-opacity': state.basinsVisible ? 1 : 0,
        },
      });

      // Labels: area only (no A1/B1), hectares with PT decimal comma + lowercase
      // "ha" (SI). Keep a thin white halo for legibility (v0.2 had it off).
      map.addLayer({
        id: 'basins-labels',
        type: 'symbol',
        source: 'basins-src',
        'source-layer': state.basinsSourceLayer,
        filter: itemFilter,
        layout: {
          'symbol-placement': 'point',
          'text-field': ['concat',
            ['number-format', ['get', 'area_ha'],
              { 'locale': 'pt-PT', 'min-fraction-digits': 1, 'max-fraction-digits': 1 }],
            ' ha'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 11,
          'text-allow-overlap': false,
          visibility: state.basinsVisible ? 'visible' : 'none',
        },
        paint: {
          'text-color': '#323232',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.2,
        },
      });

      console.log('Basins vector layers added to 2D map');
    };

    // Add flood zones fill + outline + label layers (GFI-based risk polygons)
    const addFloodZonesLayers = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      if (!state.floodZonesTileUrl || !state.floodZonesSourceLayer || !state.floodZonesItemId) return;
      if (map.getLayer('flood-zones-fill')) return;

      if (!map.getSource('flood-zones-src')) map.addSource('flood-zones-src', {
        type: 'vector',
        tiles: [state.floodZonesTileUrl],
        minzoom: 0,
        maxzoom: 16,
      });

      const beforeLayer = map.getLayer('order-polygon-fill') ? 'order-polygon-fill' : undefined;
      const itemFilter = ['==', ['to-string', ['get', 'order_item_id']], state.floodZonesItemId];

      // Fill — red/orange/yellow by risk class
      map.addLayer({
        id: 'flood-zones-fill',
        type: 'fill',
        source: 'flood-zones-src',
        'source-layer': state.floodZonesSourceLayer,
        filter: itemFilter,
        paint: {
          'fill-color': ['match', ['get', 'risk_class'],
            'high', '#d73027',
            'medium', '#fc8d59',
            'low', '#fee08b',
            '#cccccc',
          ],
          'fill-opacity': state.floodZonesVisible ? state.floodZonesOpacity : 0,
        },
      }, beforeLayer);

      // Outline
      map.addLayer({
        id: 'flood-zones-outline',
        type: 'line',
        source: 'flood-zones-src',
        'source-layer': state.floodZonesSourceLayer,
        filter: itemFilter,
        paint: {
          'line-color': ['match', ['get', 'risk_class'],
            'high', '#a50026',
            'medium', '#d73027',
            'low', '#f46d43',
            '#999999',
          ],
          'line-width': 1.5,
          'line-opacity': state.floodZonesVisible ? 1 : 0,
        },
      }, beforeLayer);

      // Labels
      map.addLayer({
        id: 'flood-zones-labels',
        type: 'symbol',
        source: 'flood-zones-src',
        'source-layer': state.floodZonesSourceLayer,
        filter: itemFilter,
        layout: {
          'symbol-placement': 'point',
          'text-field': ['concat', ['get', 'risk_label'], '\n', ['to-string', ['round', ['get', 'area_ha']]], ' Ha'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 10,
          'text-allow-overlap': false,
          visibility: state.floodZonesVisible ? 'visible' : 'none',
        },
        paint: {
          'text-color': '#7f0000',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.5,
        },
      });

      console.log('Flood zones vector layers added to 2D map');
    };

    // Add keypoints circle + label layers (valley inflection points)
    const addKeypointsLayers = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      if (!state.keypointsTileUrl || !state.keypointsSourceLayer || !state.keypointsItemId) return;
      if (map.getLayer('keypoints-circle')) return;

      if (!map.getSource('keypoints-src')) map.addSource('keypoints-src', {
        type: 'vector',
        tiles: [state.keypointsTileUrl],
        minzoom: 0,
        maxzoom: 16,
      });

      const itemFilter = ['==', ['to-string', ['get', 'order_item_id']], state.keypointsItemId];

      // Gold circles with white halo
      map.addLayer({
        id: 'keypoints-circle',
        type: 'circle',
        source: 'keypoints-src',
        'source-layer': state.keypointsSourceLayer,
        filter: itemFilter,
        paint: {
          'circle-radius': 6,
          'circle-color': '#D4A843',
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 2,
          'circle-opacity': state.keypointsVisible ? state.keypointsOpacity : 0,
          'circle-stroke-opacity': state.keypointsVisible ? state.keypointsOpacity : 0,
        },
      });

      // Elevation labels
      map.addLayer({
        id: 'keypoints-labels',
        type: 'symbol',
        source: 'keypoints-src',
        'source-layer': state.keypointsSourceLayer,
        filter: itemFilter,
        layout: {
          'text-field': ['concat', ['to-string', ['round', ['get', 'elevation']]], 'm'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 10,
          'text-offset': [0, 1.5],
          'text-allow-overlap': false,
          visibility: state.keypointsVisible ? 'visible' : 'none',
        },
        paint: {
          'text-color': '#8B6914',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.5,
        },
      });

      console.log('Keypoints vector layers added to 2D map');
    };

    // Add primary valleys fill + outline layers (geomorphon class 9 polygons)
    const addPrimaryValleysLayers = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      if (!state.primaryValleysTileUrl || !state.primaryValleysSourceLayer || !state.primaryValleysItemId) return;
      if (map.getLayer('primary-valleys-fill')) return;

      if (!map.getSource('primary-valleys-src')) map.addSource('primary-valleys-src', {
        type: 'vector',
        tiles: [state.primaryValleysTileUrl],
        minzoom: 0,
        maxzoom: 16,
      });

      const beforeLayer = map.getLayer('order-polygon-fill') ? 'order-polygon-fill' : undefined;
      const itemFilter = ['==', ['to-string', ['get', 'order_item_id']], state.primaryValleysItemId];

      map.addLayer({
        id: 'primary-valleys-fill',
        type: 'fill',
        source: 'primary-valleys-src',
        'source-layer': state.primaryValleysSourceLayer,
        filter: itemFilter,
        paint: {
          'fill-color': '#698195',
          'fill-opacity': state.primaryValleysVisible ? state.primaryValleysOpacity : 0,
        },
      }, beforeLayer);

      map.addLayer({
        id: 'primary-valleys-outline',
        type: 'line',
        source: 'primary-valleys-src',
        'source-layer': state.primaryValleysSourceLayer,
        filter: itemFilter,
        paint: {
          'line-color': '#4a6070',
          'line-width': 1,
          'line-opacity': state.primaryValleysVisible ? 1 : 0,
        },
      }, beforeLayer);

      console.log('Primary valleys vector layers added to 2D map');
    };

    // Add primary ridges fill + outline layers (geomorphon class 3 polygons)
    const addPrimaryRidgesLayers = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      if (!state.primaryRidgesTileUrl || !state.primaryRidgesSourceLayer || !state.primaryRidgesItemId) return;
      if (map.getLayer('primary-ridges-fill')) return;

      if (!map.getSource('primary-ridges-src')) map.addSource('primary-ridges-src', {
        type: 'vector',
        tiles: [state.primaryRidgesTileUrl],
        minzoom: 0,
        maxzoom: 16,
      });

      const beforeLayer = map.getLayer('order-polygon-fill') ? 'order-polygon-fill' : undefined;
      const itemFilter = ['==', ['to-string', ['get', 'order_item_id']], state.primaryRidgesItemId];

      map.addLayer({
        id: 'primary-ridges-fill',
        type: 'fill',
        source: 'primary-ridges-src',
        'source-layer': state.primaryRidgesSourceLayer,
        filter: itemFilter,
        paint: {
          'fill-color': '#ba2231',
          'fill-opacity': state.primaryRidgesVisible ? state.primaryRidgesOpacity : 0,
        },
      }, beforeLayer);

      map.addLayer({
        id: 'primary-ridges-outline',
        type: 'line',
        source: 'primary-ridges-src',
        'source-layer': state.primaryRidgesSourceLayer,
        filter: itemFilter,
        paint: {
          'line-color': '#8a1a25',
          'line-width': 1,
          'line-opacity': state.primaryRidgesVisible ? 1 : 0,
        },
      }, beforeLayer);

      console.log('Primary ridges vector layers added to 2D map');
    };

    // Add OSM roads context layer (PMTiles, always available, below analysis layers)
    const addRoadsContextLayers = () => {
      if (!map.getStyle()) return;
      if (map.getLayer('osm-roads-motorway')) return;

      if (!map.getSource('osm-portugal')) {
        map.addSource('osm-portugal', {
          type: 'vector',
          url: OSM_PMTILES_URL,
        });
      }

      const state = useAtlasStore.getState();

      // Insert roads above basemap but below all analysis layers
      const layers = map.getStyle().layers;
      let insertBefore: string | undefined;
      for (const l of layers) {
        if (l.id !== 'basemap-layer') {
          insertBefore = l.id;
          break;
        }
      }

      const roadStyles = [
        { id: 'osm-roads-motorway', filter: ['motorway', 'motorway_link'], color: '#333333', width: [3, 6] },
        { id: 'osm-roads-trunk', filter: ['trunk', 'trunk_link'], color: '#444444', width: [2.5, 5] },
        { id: 'osm-roads-primary', filter: ['primary', 'primary_link'], color: '#555555', width: [2, 4] },
        { id: 'osm-roads-secondary', filter: ['secondary', 'secondary_link'], color: '#666666', width: [1.5, 3] },
        { id: 'osm-roads-tertiary', filter: ['tertiary', 'tertiary_link'], color: '#777777', width: [1, 2.5] },
        { id: 'osm-roads-residential', filter: ['residential', 'service', 'unclassified'], color: '#888888', width: [0.5, 2] },
        { id: 'osm-roads-track', filter: ['track', 'path', 'footway', 'cycleway'], color: '#999999', width: [0.3, 1] },
      ];

      for (const s of roadStyles) {
        map.addLayer({
          id: s.id,
          type: 'line',
          source: 'osm-portugal',
          'source-layer': 'roads',
          filter: ['in', ['get', 'kind_detail'], ['literal', s.filter]],
          paint: {
            'line-color': s.color,
            'line-width': ['interpolate', ['linear'], ['zoom'], 6, s.width[0], 16, s.width[1]],
            'line-opacity': state.roadsVisible ? state.roadsOpacity : 0,
          },
          layout: { 'line-cap': 'round', 'line-join': 'round' },
        }, insertBefore);
      }

      console.log('OSM Roads context layers added to 2D map');
    };

    // Add OSM buildings context layer (PMTiles, grey fill polygons)
    const addBuildingsContextLayers = () => {
      if (!map.getStyle()) return;
      if (map.getLayer('osm-buildings-fill')) return;

      // Source already added by addRoadsContextLayers, but add if roads are disabled
      if (!map.getSource('osm-portugal')) {
        map.addSource('osm-portugal', {
          type: 'vector',
          url: OSM_PMTILES_URL,
        });
      }

      const state = useAtlasStore.getState();
      const layers = map.getStyle().layers;
      let insertBefore: string | undefined;
      for (const l of layers) {
        if (l.id !== 'basemap-layer') {
          insertBefore = l.id;
          break;
        }
      }

      map.addLayer({
        id: 'osm-buildings-fill',
        type: 'fill',
        source: 'osm-portugal',
        'source-layer': 'buildings',
        filter: ['==', ['get', 'kind'], 'building'],
        paint: {
          'fill-color': '#808080',
          'fill-opacity': state.buildingsVisible ? state.buildingsOpacity : 0,
        },
      }, insertBefore);

      map.addLayer({
        id: 'osm-buildings-outline',
        type: 'line',
        source: 'osm-portugal',
        'source-layer': 'buildings',
        filter: ['==', ['get', 'kind'], 'building'],
        paint: {
          'line-color': '#666666',
          'line-width': 0.5,
          'line-opacity': state.buildingsVisible ? 1 : 0,
        },
      }, insertBefore);

      console.log('OSM Buildings context layers added to 2D map');
    };

    // Add OSM land use context layer (PMTiles, categorized fill: forest/agriculture/urban)
    const addLanduseContextLayers = () => {
      if (!map.getStyle()) return;
      if (map.getLayer('osm-landuse-fill')) return;

      if (!map.getSource('osm-portugal')) {
        map.addSource('osm-portugal', { type: 'vector', url: OSM_PMTILES_URL });
      }

      const state = useAtlasStore.getState();
      const layers = map.getStyle().layers;
      let insertBefore: string | undefined;
      for (const l of layers) {
        if (l.id !== 'basemap-layer') { insertBefore = l.id; break; }
      }

      map.addLayer({
        id: 'osm-landuse-fill',
        type: 'fill',
        source: 'osm-portugal',
        'source-layer': 'landuse',
        filter: ['in', ['get', 'kind'], ['literal', ['forest', 'wood', 'farmland', 'farmyard', 'orchard', 'meadow', 'grass', 'residential', 'commercial', 'industrial']]],
        paint: {
          'fill-color': ['match', ['get', 'kind'],
            'forest', '#5A8A42', 'wood', '#5A8A42',
            'farmland', '#D4C878', 'farmyard', '#D4C878', 'orchard', '#D4C878', 'meadow', '#D4C878', 'grass', '#D4C878',
            'residential', '#A0A0A0', 'commercial', '#A0A0A0', 'industrial', '#A0A0A0',
            '#cccccc',
          ],
          'fill-opacity': state.landuseVisible ? state.landuseOpacity : 0,
        },
      }, insertBefore);

      console.log('OSM Land use context layer added to 2D map');
    };

    // Add OSM waterways context layer (PMTiles, blue lines for rivers/streams/canals)
    const addWaterwaysContextLayers = () => {
      if (!map.getStyle()) return;
      if (map.getLayer('osm-waterways-line')) return;

      if (!map.getSource('osm-portugal')) {
        map.addSource('osm-portugal', { type: 'vector', url: OSM_PMTILES_URL });
      }

      const state = useAtlasStore.getState();
      const layers = map.getStyle().layers;
      let insertBefore: string | undefined;
      for (const l of layers) {
        if (l.id !== 'basemap-layer') { insertBefore = l.id; break; }
      }

      map.addLayer({
        id: 'osm-waterways-line',
        type: 'line',
        source: 'osm-portugal',
        'source-layer': 'water',
        filter: ['in', ['get', 'kind'], ['literal', ['stream', 'river', 'canal']]],
        paint: {
          'line-color': '#6baed6',
          'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 14, 1.5],
          'line-opacity': state.waterwaysVisible ? state.waterwaysOpacity : 0,
        },
        layout: { 'line-cap': 'round', 'line-join': 'round' },
      }, insertBefore);

      console.log('OSM Waterways context layer added to 2D map');
    };

    // Add OSM admin boundaries context layer (PMTiles, dashed grey lines)
    const addAdminBoundsContextLayers = () => {
      if (!map.getStyle()) return;
      if (map.getLayer('osm-admin-bounds-line')) return;

      if (!map.getSource('osm-portugal')) {
        map.addSource('osm-portugal', { type: 'vector', url: OSM_PMTILES_URL });
      }

      const state = useAtlasStore.getState();
      const layers = map.getStyle().layers;
      let insertBefore: string | undefined;
      for (const l of layers) {
        if (l.id !== 'basemap-layer') { insertBefore = l.id; break; }
      }

      map.addLayer({
        id: 'osm-admin-bounds-line',
        type: 'line',
        source: 'osm-portugal',
        'source-layer': 'boundaries',
        paint: {
          'line-color': '#555555',
          'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1.5, 14, 2.5],
          'line-opacity': state.adminBoundsVisible ? state.adminBoundsOpacity : 0,
          'line-dasharray': [6, 4],
        },
        layout: { 'line-cap': 'round', 'line-join': 'round' },
      }, insertBefore);

      console.log('OSM Admin boundaries context layer added to 2D map');
    };

    // === Institutional layers (PR5b) ===
    // Cadastro DGT. Lines, NIC label at z>=17. Always-on global context.
    const addCadastroDgtLayer = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      if (!state.cadastroDgtTileUrl || !state.cadastroDgtSourceLayer) return;
      // Wait for the order-scoped bounds before creating the source: a vector
      // source's `bounds` is immutable after creation, and an unbounded
      // cadastro source would fetch tiles across the whole country. The bounds
      // are the union extent of parcels intersecting the order (+500m), so
      // every intersecting parcel's tiles load and none is cut at the edge.
      if (!state.cadastroBounds) return;
      if (map.getLayer('cadastro-dgt-line')) return;
      if (!map.getSource('cadastro-dgt-src')) map.addSource('cadastro-dgt-src', {
        type: 'vector',
        tiles: [state.cadastroDgtTileUrl],
        minzoom: 12,
        maxzoom: 18,
        bounds: state.cadastroBounds,
      });
      const beforeLayer = map.getLayer('order-polygon-fill') ? 'order-polygon-fill' : undefined;
      map.addLayer({
        id: 'cadastro-dgt-line',
        type: 'line',
        source: 'cadastro-dgt-src',
        'source-layer': state.cadastroDgtSourceLayer,
        minzoom: 12,
        paint: {
          'line-color': '#2a2a2a',
          'line-width': 1.5,
          'line-opacity': state.cadastroDgtVisible ? state.cadastroDgtOpacity : 0,
        },
      }, beforeLayer);
      // Transparent fill to capture interior clicks for the parcel popup (the
      // visible cadastro layer is line-only, too thin to click reliably).
      map.addLayer({
        id: 'cadastro-dgt-fill',
        type: 'fill',
        source: 'cadastro-dgt-src',
        'source-layer': state.cadastroDgtSourceLayer,
        minzoom: 12,
        paint: { 'fill-color': '#000000', 'fill-opacity': 0 },
      }, beforeLayer);
      map.addLayer({
        id: 'cadastro-dgt-labels',
        type: 'symbol',
        source: 'cadastro-dgt-src',
        'source-layer': state.cadastroDgtSourceLayer,
        minzoom: 17,
        layout: {
          'symbol-placement': 'point',
          'text-field': ['get', 'national_cadastral_reference'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 10,
          'text-allow-overlap': false,
          visibility: state.cadastroDgtVisible ? 'visible' : 'none',
        },
        paint: {
          'text-color': '#2a2a2a',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.5,
        },
      });
      console.log('Cadastro DGT vector layer added to 2D map');
    };

    // BUPi RGG. Dashed lines, self-declared parcel registry.
    const addBupiRggLayer = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      if (!state.bupiRggTileUrl || !state.bupiRggSourceLayer) return;
      if (map.getLayer('bupi-rgg-line')) return;
      if (!map.getSource('bupi-rgg-src')) map.addSource('bupi-rgg-src', {
        type: 'vector',
        tiles: [state.bupiRggTileUrl],
        minzoom: 12,
        maxzoom: 18,
      });
      const beforeLayer = map.getLayer('order-polygon-fill') ? 'order-polygon-fill' : undefined;
      map.addLayer({
        id: 'bupi-rgg-line',
        type: 'line',
        source: 'bupi-rgg-src',
        'source-layer': state.bupiRggSourceLayer,
        minzoom: 12,
        paint: {
          'line-color': '#6b4f3a',
          'line-width': 1,
          'line-dasharray': [2, 2],
          'line-opacity': state.bupiRggVisible ? state.bupiRggOpacity : 0,
        },
      }, beforeLayer);
      // Transparent fill to capture interior clicks for the artigo popup (the
      // visible BUPi layer is dashed-line-only, too thin to click reliably).
      map.addLayer({
        id: 'bupi-rgg-fill',
        type: 'fill',
        source: 'bupi-rgg-src',
        'source-layer': state.bupiRggSourceLayer,
        minzoom: 12,
        paint: { 'fill-color': '#000000', 'fill-opacity': 0 },
      }, beforeLayer);
      console.log('BUPi RGG vector layer added to 2D map');
    };

    // CAOP admin limits. Single source, two layers filtered by `tipo`.
    const addCaopLayers = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      if (!state.freguesiasTileUrl || !state.freguesiasSourceLayer) return;
      if (map.getLayer('caop-freguesias-line')) return;
      if (!map.getSource('caop-src')) map.addSource('caop-src', {
        type: 'vector',
        tiles: [state.freguesiasTileUrl],
        minzoom: 0,
        maxzoom: 18,
      });
      const beforeLayer = map.getLayer('order-polygon-fill') ? 'order-polygon-fill' : undefined;
      map.addLayer({
        id: 'caop-freguesias-line',
        type: 'line',
        source: 'caop-src',
        'source-layer': state.freguesiasSourceLayer,
        filter: ['==', ['get', 'tipo'], 'Freguesia'] as any,
        paint: {
          'line-color': '#7c3aed',
          'line-width': 1.5,
          'line-opacity': state.freguesiasVisible ? state.freguesiasOpacity : 0,
        },
      }, beforeLayer);
      map.addLayer({
        id: 'caop-concelhos-line',
        type: 'line',
        source: 'caop-src',
        'source-layer': state.concelhosSourceLayer || state.freguesiasSourceLayer,
        filter: ['==', ['get', 'tipo'], 'Concelho'] as any,
        paint: {
          'line-color': '#7c3aed',
          'line-width': 3,
          'line-opacity': state.concelhosVisible ? state.concelhosOpacity : 0,
        },
      }, beforeLayer);
      console.log('CAOP admin vector layers added to 2D map');
    };

    // REN. Two sub-layers (Reserva Ecologica Nacional fill + Exclusoes
    // outline). Source + property names vary between global geo.ren and
    // per-order order_restricoes_legais clip.
    const addRenLayers = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      if (!state.renTileUrl) return;
      // Derive clipped-ness + source-layer + property names from the tile URL,
      // which is always internally consistent. Reading state.renSourceLayer
      // here is a trap: App.tsx sets tileUrl + sourceLayer + itemId as separate
      // store writes, each firing the addAllLayers subscriber, so the layer can
      // be created on the tileUrl write while sourceLayer still holds its
      // default. The idempotency guard then locks in the stale source-layer.
      const clipped = state.renTileUrl.includes('order_restricoes_legais');
      // When clipped, wait until the order item id is known so the per-item
      // filter is correct (avoids showing every order's clip at once).
      if (clipped && !state.renItemId) return;
      if (map.getLayer('ren-fill')) return;
      const sourceLayerName = clipped ? 'order_restricoes_legais' : 'ren';
      if (!map.getSource('ren-src')) map.addSource('ren-src', {
        type: 'vector',
        tiles: [state.renTileUrl],
        minzoom: 0,
        maxzoom: 18,
      });
      const beforeLayer = map.getLayer('order-polygon-fill') ? 'order-polygon-fill' : undefined;
      const tipologiaProp = clipped ? 'ren_tipologia' : 'tipologia';
      // Common item-id constraint (only when clipped).
      const baseFilter: any[] = ['all'];
      if (clipped && state.renItemId) {
        baseFilter.push(['==', ['to-string', ['get', 'order_item_id']], state.renItemId]);
        baseFilter.push(['==', ['get', 'source'], 'ren']);
      }
      const renFilter = [...baseFilter, ['==', ['get', tipologiaProp], 'Reserva Ecológica Nacional']] as any;
      const exclFilter = [...baseFilter, ['==', ['get', tipologiaProp], 'Exclusões']] as any;
      map.addLayer({
        id: 'ren-fill',
        type: 'fill',
        source: 'ren-src',
        'source-layer': sourceLayerName,
        filter: renFilter,
        paint: {
          'fill-color': '#2d6a4f',
          'fill-opacity': state.renVisible ? state.renOpacity : 0,
        },
      }, beforeLayer);
      map.addLayer({
        id: 'ren-exclusoes-line',
        type: 'line',
        source: 'ren-src',
        'source-layer': sourceLayerName,
        filter: exclFilter,
        paint: {
          'line-color': '#2d6a4f',
          'line-width': 1.5,
          'line-dasharray': [2, 1],
          'line-opacity': state.renExclusoesVisible ? state.renExclusoesOpacity : 0,
        },
      }, beforeLayer);
      console.log(`REN vector layers added to 2D map (${clipped ? 'clipped' : 'global'})`);
    };

    // RAN. Single fill layer.
    const addRanLayer = () => {
      if (!map.getStyle()) return;
      const state = useAtlasStore.getState();
      if (!state.ranTileUrl) return;
      // Derive from tileUrl, not state.ranSourceLayer (set-ordering trap, see
      // addRenLayers note).
      const clipped = state.ranTileUrl.includes('order_restricoes_legais');
      if (clipped && !state.ranItemId) return;
      if (map.getLayer('ran-fill')) return;
      const sourceLayerName = clipped ? 'order_restricoes_legais' : 'ran';
      if (!map.getSource('ran-src')) map.addSource('ran-src', {
        type: 'vector',
        tiles: [state.ranTileUrl],
        minzoom: 0,
        maxzoom: 18,
      });
      const beforeLayer = map.getLayer('order-polygon-fill') ? 'order-polygon-fill' : undefined;
      let ranFilter: any;
      if (clipped && state.ranItemId) {
        ranFilter = ['all',
          ['==', ['to-string', ['get', 'order_item_id']], state.ranItemId],
          ['==', ['get', 'source'], 'ran'],
        ];
      }
      map.addLayer({
        id: 'ran-fill',
        type: 'fill',
        source: 'ran-src',
        'source-layer': sourceLayerName,
        ...(ranFilter ? { filter: ranFilter } : {}),
        paint: {
          'fill-color': '#fbbf24',
          'fill-opacity': state.ranVisible ? state.ranOpacity : 0,
        },
      }, beforeLayer);
      console.log(`RAN vector layer added to 2D map (${clipped ? 'clipped' : 'global'})`);
    };

    // Wrap each layer-add in try/catch so one failure doesn't block the rest.
    // EB-56 lazy loading: vector/institutional/OSM layers are only added (and
    // therefore only fetch tiles) once their toggle is on. Rasters self-gate:
    // their tile URL is set lazily by App.loadLayer on first toggle, so
    // addMissingLayers does nothing for them until then.
    const addAllLayers = () => {
      const st = useAtlasStore.getState();
      const g = (on: boolean, fn: () => void, name: string) => {
        if (!on) return;
        try { fn(); } catch (e) { console.warn(`${name} error:`, e); }
      };
      g(st.roadsVisible, addRoadsContextLayers, 'addRoadsContextLayers');
      g(st.buildingsVisible, addBuildingsContextLayers, 'addBuildingsContextLayers');
      g(st.landuseVisible, addLanduseContextLayers, 'addLanduseContextLayers');
      g(st.waterwaysVisible, addWaterwaysContextLayers, 'addWaterwaysContextLayers');
      g(st.adminBoundsVisible, addAdminBoundsContextLayers, 'addAdminBoundsContextLayers');
      try { addMissingLayers(); } catch (e) { console.warn('addMissingLayers error:', e); }
      g(st.contoursVisible, addContourDgtLayers, 'addContourDgtLayers');
      g(st.streamsVisible, addStreamsLayers, 'addStreamsLayers');
      g(st.ridgesVisible, addRidgesLayers, 'addRidgesLayers');
      g(st.basinsVisible, addBasinsLayers, 'addBasinsLayers');
      g(st.floodZonesVisible, addFloodZonesLayers, 'addFloodZonesLayers');
      g(st.keypointsVisible, addKeypointsLayers, 'addKeypointsLayers');
      g(st.primaryValleysVisible, addPrimaryValleysLayers, 'addPrimaryValleysLayers');
      g(st.primaryRidgesVisible, addPrimaryRidgesLayers, 'addPrimaryRidgesLayers');
      g(st.cadastroDgtVisible, addCadastroDgtLayer, 'addCadastroDgtLayer');
      g(st.bupiRggVisible, addBupiRggLayer, 'addBupiRggLayer');
      g(st.freguesiasVisible || st.concelhosVisible, addCaopLayers, 'addCaopLayers');
      g(st.renVisible || st.renExclusoesVisible, addRenLayers, 'addRenLayers');
      g(st.ranVisible, addRanLayer, 'addRanLayer');
    };
    map.once('load', addAllLayers);
    const unsub = useAtlasStore.subscribe(addAllLayers);
    // Poll as fallback — subscribe may not fire reliably for late store updates
    const interval = setInterval(addAllLayers, 500);
    const stopPolling = setTimeout(() => clearInterval(interval), 15000);

    return () => {
      unsub();
      clearInterval(interval);
      clearTimeout(stopPolling);
      map.remove();
      mapRef.current = null;
      polygonAddedRef.current = false;
      maplibregl.removeProtocol('pmtiles');
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Add polygon when order data is available
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !orderPolygon || polygonAddedRef.current) return;

    const addPolygon = () => {
      if (polygonAddedRef.current) return;

      map.addSource('order-polygon', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: orderPolygon,
          properties: {},
        },
      });

      map.addLayer({
        id: 'order-polygon-fill',
        type: 'fill',
        source: 'order-polygon',
        paint: {
          'fill-color': '#2196F3',
          // EB-73: lighter veil (was 0.25) so it highlights the parcel without
          // washing out analysis layers. Lines/rasters now draw above it anyway.
          'fill-opacity': 0.1,
        },
      });

      map.addLayer({
        id: 'order-polygon-outline',
        type: 'line',
        source: 'order-polygon',
        paint: {
          'line-color': '#1565C0',
          'line-width': 3,
        },
      });

      polygonAddedRef.current = true;
    };

    if (map.isStyleLoaded()) {
      addPolygon();
    } else {
      map.once('load', addPolygon);
    }
  }, [orderPolygon]);

  // Fly to bounds on first load
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !orderBounds) return;

    const [west, south, east, north] = orderBounds;

    const flyToBounds = () => {
      map.fitBounds(
        [[west, south], [east, north]],
        { padding: 60, duration: 1500 }
      );
    };

    if (map.isStyleLoaded()) {
      flyToBounds();
    } else {
      map.once('load', flyToBounds);
    }
  }, [orderBounds]);

  // --- Create-order draw: crosshair cursor while drawing ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const canvas = map.getCanvas();
    canvas.style.cursor = drawing ? 'crosshair' : '';
    return () => { canvas.style.cursor = ''; };
  }, [drawing]);

  // --- Create-order draw: capture map clicks as polygon vertices ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !drawing) return;
    const onClick = (e: maplibregl.MapMouseEvent) => {
      addDraftPoint([e.lngLat.lng, e.lngLat.lat]);
    };
    map.on('click', onClick);
    return () => { map.off('click', onClick); };
  }, [drawing, addDraftPoint]);

  // --- Create-order draw: render the draft polygon + vertices ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const render = () => {
      // An uploaded polygon (draftGeometry) takes precedence over drawn points.
      const pts = draftGeometry ? [] : draftPoints;
      const polyGeom: GeoJSON.Polygon = draftGeometry
        ? draftGeometry
        : { type: 'Polygon', coordinates: [pts.length >= 3 ? [...pts, pts[0]] : pts] };
      const polyData: GeoJSON.Feature = {
        type: 'Feature',
        properties: {},
        geometry: polyGeom,
      };
      const ptData: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: pts.map((p, i) => ({
          type: 'Feature',
          properties: { i },
          geometry: { type: 'Point', coordinates: p },
        })),
      };
      if (!map.getSource('draft-polygon')) {
        map.addSource('draft-polygon', { type: 'geojson', data: polyData });
        map.addLayer({
          id: 'draft-polygon-fill',
          type: 'fill',
          source: 'draft-polygon',
          paint: { 'fill-color': '#C4654A', 'fill-opacity': 0.18 },
        });
        map.addLayer({
          id: 'draft-polygon-outline',
          type: 'line',
          source: 'draft-polygon',
          paint: { 'line-color': '#C4654A', 'line-width': 2.5 },
        });
      } else {
        (map.getSource('draft-polygon') as maplibregl.GeoJSONSource).setData(polyData);
      }
      if (!map.getSource('draft-points')) {
        map.addSource('draft-points', { type: 'geojson', data: ptData });
        map.addLayer({
          id: 'draft-points-layer',
          type: 'circle',
          source: 'draft-points',
          paint: {
            'circle-radius': 5,
            'circle-color': '#C4654A',
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 2,
          },
        });
      } else {
        (map.getSource('draft-points') as maplibregl.GeoJSONSource).setData(ptData);
      }
    };
    if (map.isStyleLoaded()) render();
    else map.once('load', render);
  }, [draftPoints, draftGeometry]);

  // Toggle DGT raster layer visibility + opacity
  useEffect(() => { const map = mapRef.current; if (!map) return; try { if (map.getLayer('mdt-layer')) map.setPaintProperty('mdt-layer', 'raster-opacity', mdtVisible ? mdtOpacity : 0); if (map.getLayer('mdt-hillshade-layer')) map.setLayoutProperty('mdt-hillshade-layer', 'visibility', (mdtVisible && mdtOpacity > 0) ? 'visible' : 'none'); } catch {} }, [mdtVisible, mdtOpacity]);
  useEffect(() => { const map = mapRef.current; if (!map) return; try { if (map.getLayer('hillshade-layer')) map.setPaintProperty('hillshade-layer', 'raster-opacity', hillshadeVisible ? hillshadeOpacity : 0); } catch {} }, [hillshadeVisible, hillshadeOpacity]);
  useEffect(() => { const map = mapRef.current; if (!map) return; try { if (map.getLayer('hillshade-hypso-layer')) map.setPaintProperty('hillshade-hypso-layer', 'raster-opacity', hillshadeHypsoVisible ? hillshadeHypsoOpacity : 0); } catch {} }, [hillshadeHypsoVisible, hillshadeHypsoOpacity]);

  // Toggle slope visibility + opacity
  useEffect(() => { const map = mapRef.current; if (!map) return; try { if (map.getLayer('slope-layer')) map.setPaintProperty('slope-layer', 'raster-opacity', slopeVisible ? slopeOpacity : 0); } catch {} }, [slopeVisible, slopeOpacity]);
  // Toggle aspect visibility + opacity
  useEffect(() => { const map = mapRef.current; if (!map) return; try { if (map.getLayer('aspect-layer')) map.setPaintProperty('aspect-layer', 'raster-opacity', aspectVisible ? aspectOpacity : 0); } catch {} }, [aspectVisible, aspectOpacity]);
  // Toggle geomorphons visibility + opacity
  useEffect(() => { const map = mapRef.current; if (!map) return; try { if (map.getLayer('geomorphons-layer')) map.setPaintProperty('geomorphons-layer', 'raster-opacity', geomorphonsVisible ? geomorphonsOpacity : 0); } catch {} }, [geomorphonsVisible, geomorphonsOpacity]);
  // Toggle wind exposition visibility + opacity (hillshade underlay + exposition overlay)
  // Baked relief: one layer, opacity follows the slider directly (the colour
  // and the shading are already composited, so there is no stack to balance).
  useEffect(() => { const map = mapRef.current; if (!map) return; try { if (map.getLayer('wind-relief-layer')) map.setPaintProperty('wind-relief-layer', 'raster-opacity', windExpositionVisible ? windExpositionOpacity : 0); } catch {} }, [windExpositionVisible, windExpositionOpacity]);
  useEffect(() => { const map = mapRef.current; if (!map) return; try { if (map.getLayer('wind-hillshade-layer')) map.setPaintProperty('wind-hillshade-layer', 'raster-opacity', windExpositionVisible ? 1 : 0); } catch {} }, [windExpositionVisible]);
  useEffect(() => { const map = mapRef.current; if (!map) return; try { if (map.getLayer('wind-exposition-layer')) map.setPaintProperty('wind-exposition-layer', 'raster-opacity', windExpositionVisible ? windExpositionOpacity : 0); } catch {} }, [windExpositionVisible, windExpositionOpacity]);
  // Toggle keypoints visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('keypoints-circle')) {
        map.setPaintProperty('keypoints-circle', 'circle-opacity', keypointsVisible ? keypointsOpacity : 0);
        map.setPaintProperty('keypoints-circle', 'circle-stroke-opacity', keypointsVisible ? keypointsOpacity : 0);
      }
      if (map.getLayer('keypoints-labels')) map.setLayoutProperty('keypoints-labels', 'visibility', keypointsVisible ? 'visible' : 'none');
    } catch {}
  }, [keypointsVisible, keypointsOpacity]);
  // Toggle primary valleys visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('primary-valleys-fill')) map.setPaintProperty('primary-valleys-fill', 'fill-opacity', primaryValleysVisible ? primaryValleysOpacity : 0);
      if (map.getLayer('primary-valleys-outline')) map.setPaintProperty('primary-valleys-outline', 'line-opacity', primaryValleysVisible ? 1 : 0);
    } catch {}
  }, [primaryValleysVisible, primaryValleysOpacity]);
  // Toggle primary ridges visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('primary-ridges-fill')) map.setPaintProperty('primary-ridges-fill', 'fill-opacity', primaryRidgesVisible ? primaryRidgesOpacity : 0);
      if (map.getLayer('primary-ridges-outline')) map.setPaintProperty('primary-ridges-outline', 'line-opacity', primaryRidgesVisible ? 1 : 0);
    } catch {}
  }, [primaryRidgesVisible, primaryRidgesOpacity]);
  // Toggle erosion risk visibility + opacity
  useEffect(() => { const map = mapRef.current; if (!map) return; try { if (map.getLayer('erosion-risk-layer')) map.setPaintProperty('erosion-risk-layer', 'raster-opacity', erosionRiskVisible ? erosionRiskOpacity : 0); } catch {} }, [erosionRiskVisible, erosionRiskOpacity]);
  // Toggle flow accumulation visibility + opacity
  useEffect(() => { const map = mapRef.current; if (!map) return; try { if (map.getLayer('flow-acc-layer')) map.setPaintProperty('flow-acc-layer', 'raster-opacity', flowAccVisible ? flowAccOpacity : 0); } catch {} }, [flowAccVisible, flowAccOpacity]);
  // Toggle streams visibility + opacity (single continuous line layer, EB-73)
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('streams-line')) map.setPaintProperty('streams-line', 'line-opacity', streamsVisible ? streamsOpacity : 0);
    } catch {}
  }, [streamsVisible, streamsOpacity]);
  // Toggle ridges visibility + opacity (single continuous line layer, EB-73)
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('ridges-line')) map.setPaintProperty('ridges-line', 'line-opacity', ridgesVisible ? ridgesOpacity : 0);
    } catch {}
  }, [ridgesVisible, ridgesOpacity]);
  // Toggle basins visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('basins-fill')) map.setPaintProperty('basins-fill', 'fill-opacity', basinsVisible ? basinsOpacity : 0);
      if (map.getLayer('basins-outline')) map.setPaintProperty('basins-outline', 'line-opacity', basinsVisible ? 1 : 0);
      if (map.getLayer('basins-labels')) map.setLayoutProperty('basins-labels', 'visibility', basinsVisible ? 'visible' : 'none');
    } catch {}
  }, [basinsVisible, basinsOpacity]);
  // Toggle flood zones visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('flood-zones-fill')) map.setPaintProperty('flood-zones-fill', 'fill-opacity', floodZonesVisible ? floodZonesOpacity : 0);
      if (map.getLayer('flood-zones-outline')) map.setPaintProperty('flood-zones-outline', 'line-opacity', floodZonesVisible ? 1 : 0);
      if (map.getLayer('flood-zones-labels')) map.setLayoutProperty('flood-zones-labels', 'visibility', floodZonesVisible ? 'visible' : 'none');
    } catch {}
  }, [floodZonesVisible, floodZonesOpacity]);
  // Toggle Cadastro DGT visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('cadastro-dgt-line')) map.setPaintProperty('cadastro-dgt-line', 'line-opacity', cadastroDgtVisible ? cadastroDgtOpacity : 0);
      if (map.getLayer('cadastro-dgt-labels')) map.setLayoutProperty('cadastro-dgt-labels', 'visibility', cadastroDgtVisible ? 'visible' : 'none');
    } catch {}
  }, [cadastroDgtVisible, cadastroDgtOpacity]);
  // Toggle BUPi RGG visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('bupi-rgg-line')) map.setPaintProperty('bupi-rgg-line', 'line-opacity', bupiRggVisible ? bupiRggOpacity : 0);
    } catch {}
  }, [bupiRggVisible, bupiRggOpacity]);
  // Toggle CAOP freguesias visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('caop-freguesias-line')) map.setPaintProperty('caop-freguesias-line', 'line-opacity', freguesiasVisible ? freguesiasOpacity : 0);
    } catch {}
  }, [freguesiasVisible, freguesiasOpacity]);
  // Toggle CAOP concelhos visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('caop-concelhos-line')) map.setPaintProperty('caop-concelhos-line', 'line-opacity', concelhosVisible ? concelhosOpacity : 0);
    } catch {}
  }, [concelhosVisible, concelhosOpacity]);
  // Toggle REN fill visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('ren-fill')) map.setPaintProperty('ren-fill', 'fill-opacity', renVisible ? renOpacity : 0);
    } catch {}
  }, [renVisible, renOpacity]);
  // Toggle REN exclusoes line visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('ren-exclusoes-line')) map.setPaintProperty('ren-exclusoes-line', 'line-opacity', renExclusoesVisible ? renExclusoesOpacity : 0);
    } catch {}
  }, [renExclusoesVisible, renExclusoesOpacity]);
  // Toggle RAN fill visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('ran-fill')) map.setPaintProperty('ran-fill', 'fill-opacity', ranVisible ? ranOpacity : 0);
    } catch {}
  }, [ranVisible, ranOpacity]);
  // Toggle OSM roads visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      for (const id of ['osm-roads-motorway', 'osm-roads-trunk', 'osm-roads-primary', 'osm-roads-secondary', 'osm-roads-tertiary', 'osm-roads-residential', 'osm-roads-track']) {
        if (map.getLayer(id)) map.setPaintProperty(id, 'line-opacity', roadsVisible ? roadsOpacity : 0);
      }
    } catch {}
  }, [roadsVisible, roadsOpacity]);
  // Toggle OSM buildings visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('osm-buildings-fill')) map.setPaintProperty('osm-buildings-fill', 'fill-opacity', buildingsVisible ? buildingsOpacity : 0);
      if (map.getLayer('osm-buildings-outline')) map.setPaintProperty('osm-buildings-outline', 'line-opacity', buildingsVisible ? 1 : 0);
    } catch {}
  }, [buildingsVisible, buildingsOpacity]);
  // Toggle OSM land use visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('osm-landuse-fill')) map.setPaintProperty('osm-landuse-fill', 'fill-opacity', landuseVisible ? landuseOpacity : 0);
    } catch {}
  }, [landuseVisible, landuseOpacity]);
  // Toggle OSM waterways visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('osm-waterways-line')) map.setPaintProperty('osm-waterways-line', 'line-opacity', waterwaysVisible ? waterwaysOpacity : 0);
    } catch {}
  }, [waterwaysVisible, waterwaysOpacity]);
  // Toggle OSM admin boundaries visibility + opacity
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    try {
      if (map.getLayer('osm-admin-bounds-line')) map.setPaintProperty('osm-admin-bounds-line', 'line-opacity', adminBoundsVisible ? adminBoundsOpacity : 0);
    } catch {}
  }, [adminBoundsVisible, adminBoundsOpacity]);

  // Toggle DGT contour visibility + opacity
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    try {
      if (map.getLayer('contours-dgt-5m-layer'))
        map.setPaintProperty('contours-dgt-5m-layer', 'line-opacity', contoursVisible ? (['case', ['==', ['get', 'zone'], 'fringe'], contoursOpacity * 0.3, contoursOpacity] as any) : 0);
      if (map.getLayer('contours-dgt-1m-layer'))
        map.setPaintProperty('contours-dgt-1m-layer', 'line-opacity', contoursVisible ? (['case', ['==', ['get', 'zone'], 'fringe'], contoursOpacity * 0.3, contoursOpacity] as any) : 0);
      if (map.getLayer('contours-dgt-labels-layer')) {
        map.setLayoutProperty('contours-dgt-labels-layer', 'visibility', contoursVisible ? 'visible' : 'none');
      }
    } catch {}
  }, [contoursVisible, contoursOpacity]);

  // Toggle polygon visibility
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !polygonAddedRef.current) return;

    try {
      if (map.getLayer('order-polygon-fill')) {
        map.setLayoutProperty('order-polygon-fill', 'visibility', showPolygon ? 'visible' : 'none');
      }
      if (map.getLayer('order-polygon-outline')) {
        map.setLayoutProperty('order-polygon-outline', 'visibility', showPolygon ? 'visible' : 'none');
      }
    } catch {
      // Layer might not be added yet
    }
  }, [showPolygon]);

  // Switch basemap
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    const basemap = basemapFor(baseLayer);
    if (!basemap) return;

    const source = map.getSource('basemap') as maplibregl.RasterTileSource;
    if (source) {
      source.setTiles([basemap.url]);
    }
  }, [baseLayer]);

  // When switching to 2D mode, fly to stored camera position
  useEffect(() => {
    const map = mapRef.current;
    if (!map || viewMode !== '2d') return;

    // Resize map after display:none → display:block transition
    setTimeout(() => {
      map.resize();
    }, 50);
  }, [viewMode]);

  // Expose flyToOrder for Toolbar home button
  const flyToOrder = useCallback(() => {
    const map = mapRef.current;
    if (!map || !orderBounds) return;

    const [west, south, east, north] = orderBounds;
    map.fitBounds(
      [[west, south], [east, north]],
      { padding: 60, duration: 1500 }
    );
  }, [orderBounds]);

  useEffect(() => {
    (window as any).__atlas_flyToOrder2D = flyToOrder;
    return () => { delete (window as any).__atlas_flyToOrder2D; };
  }, [flyToOrder]);

  return (
    <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
  );
}
